import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import EventDetail from "./components/events/EventDetail";
import Spinner from "./components/Spinner";
import TopBar from "./components/TopBarNav";
import { auth, db } from "./firebase";
import About from "./pages/About";
import ContactPage from "./pages/Contact";
import CreateEvent from "./pages/CreateEvent";
import EventAttendees from "./pages/EventAttendees";
import Events from "./pages/Events";
import IceBreakerPage from "./pages/IceBreakerPage";
import MyMatches from "./pages/MyMatches";
import PublicUserProfile from "./pages/PublicUserProfile";
import ScrollToTop from "./utilities/ScrollToTop";

const Home = React.lazy(() => import("./pages/Home"));
const Login = React.lazy(() => import("./pages/Login"));

function App() {
	const [user, setUser] = useState(null);
	const [userDoc, setUserDoc] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
			if (currentUser) {
				setUser(currentUser);
				// ← subscribe to Firestore user doc in real-time
				const userRef = doc(db, "users", currentUser.uid);
				const unsubDoc = onSnapshot(userRef, (snap) => {
					setUserDoc(snap.exists() ? snap.data() : null);
				});
				setLoading(false);
				return () => unsubDoc();
			} else {
				setUser(null);
				setUserDoc(null);
				setLoading(false);
			}
		});
		document.body.classList.add("visible");
		return () => unsubscribe();
	}, []);

	function handleClick() {
		auth.signOut();
	}

	if (loading) {
		return <Spinner />;
	}

	return (
		<BrowserRouter>
			<ScrollToTop />
			<TopBar user={user} userDoc={userDoc} onLogout={handleClick} />
			<Suspense fallback={<Spinner />}>
				<Routes>
					<Route path="/" element={user ? <Home user={user} /> : <Navigate to="/login" />} />
					<Route path="/login" element={user ? <Navigate to="/home" /> : <Login />} />
					<Route path="/home" element={user ? <Home user={user} /> : <Navigate to="/" />} />
					<Route path="/events" element={user ? <Events user={user} /> : <Navigate to="/" />} />
					<Route path="/about" element={<About />} />
					<Route path="/contact" element={<ContactPage />} />
					<Route path="/create-event" element={user ? <CreateEvent user={user} /> : <Navigate to="/" />} />
					<Route path="/event/:eventId" element={user ? <EventDetail user={user} /> : <Navigate to="/" />} />
					<Route path="/profile/:uid" element={user ? <PublicUserProfile /> : <Navigate to="/" />} />
					<Route path="/event/:eventId/attendees" element={user ? <EventAttendees /> : <Navigate to="/" />} />
					<Route path="/icebreaker/:uid" element={user ? <IceBreakerPage /> : <Navigate to="/" />} />
					<Route path="/my-matches" element={user ? <MyMatches user={user} /> : <Navigate to="/" />} />
				</Routes>
			</Suspense>
		</BrowserRouter>
	);
}

export default App;
